import { FormEvent, useCallback, useEffect, useState } from "react";
import {
  createFood,
  deleteFood,
  getCategories,
  getFoods,
  updateFood,
} from "../service/CatalogService";
import { Category, FoodItem, FoodStatus } from "../model/types";
import { Alert } from "../util/Alert";
import { Loading } from "../util/Loading";
import { extractErrorMessage } from "../service/api";

const emptyForm = {
  name: "",
  description: "",
  price: 0,
  stockQuantity: 0,
  status: "AVAILABLE" as FoodStatus,
  categoryId: 0,
};

export const FoodsAdminPage = () => {
  const [foods, setFoods] = useState<FoodItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [foodsData, categoriesData] = await Promise.all([
        getFoods(false),
        getCategories(),
      ]);
      setFoods(foodsData);
      setCategories(categoriesData);
      setForm((prev) =>
        !prev.categoryId && categoriesData.length
          ? { ...prev, categoryId: categoriesData[0].id }
          : prev
      );
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      if (editingId) {
        await updateFood(editingId, form);
        setMessage("Food item updated");
      } else {
        await createFood(form);
        setMessage("Food item created");
      }
      setForm({
        ...emptyForm,
        categoryId: categories[0]?.id ?? 0,
      });
      setEditingId(null);
      await loadData();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const beginEdit = (food: FoodItem) => {
    setEditingId(food.id);
    setForm({
      name: food.name,
      description: food.description,
      price: food.price,
      stockQuantity: food.stockQuantity,
      status: food.status,
      categoryId: food.categoryId,
    });
  };

  const handleDelete = async (foodId: number) => {
    try {
      await deleteFood(foodId);
      setMessage("Food item deleted");
      await loadData();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  if (loading) {
    return <Loading label="Loading food items..." />;
  }

  return (
    <section className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
      <form
        onSubmit={handleSubmit}
        className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm"
      >
        <p className="text-xs uppercase tracking-[0.24em] text-emerald-700">
          Food Management
        </p>
        <h2 className="mt-2 text-2xl font-semibold text-slate-900">
          {editingId ? "Edit food item" : "Create food item"}
        </h2>
        <p className="mt-2 text-sm text-stone-600">
          Total available food units: {foods.reduce((sum, food) => sum + food.stockQuantity, 0)}
        </p>
        <div className="mt-6 space-y-4">
          {message ? (
            <Alert type="success" message={message} onClose={() => setMessage("")} />
          ) : null}
          {error ? (
            <Alert type="failed" message={error} onClose={() => setError("")} />
          ) : null}
          <label className="block space-y-2 text-sm font-medium text-slate-700">
            <span>Food name</span>
            <input
              value={form.name}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, name: event.target.value }))
              }
              placeholder="Enter food name"
              className="w-full rounded-2xl border border-stone-300 px-4 py-3"
              required
            />
          </label>
          <label className="block space-y-2 text-sm font-medium text-slate-700">
            <span>Description</span>
            <textarea
              rows={4}
              value={form.description}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, description: event.target.value }))
              }
              placeholder="Enter description"
              className="w-full rounded-2xl border border-stone-300 px-4 py-3"
              required
            />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block space-y-2 text-sm font-medium text-slate-700">
              <span>Price</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.price}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    price: Number(event.target.value),
                  }))
                }
                className="w-full rounded-2xl border border-stone-300 px-4 py-3"
                required
              />
            </label>
            <label className="block space-y-2 text-sm font-medium text-slate-700">
              <span>Stock quantity</span>
              <input
                type="number"
                min={0}
                value={form.stockQuantity}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    stockQuantity: Number(event.target.value),
                  }))
                }
                className="w-full rounded-2xl border border-stone-300 px-4 py-3"
                placeholder="Enter available units"
                required
              />
            </label>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block space-y-2 text-sm font-medium text-slate-700">
              <span>Status</span>
              <select
                value={form.status}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    status: event.target.value as "AVAILABLE" | "OUT_OF_STOCK",
                  }))
                }
                className="w-full rounded-2xl border border-stone-300 px-4 py-3"
              >
                <option value="AVAILABLE">AVAILABLE</option>
                <option value="OUT_OF_STOCK">OUT_OF_STOCK</option>
              </select>
            </label>
          </div>
          <label className="block space-y-2 text-sm font-medium text-slate-700">
            <span>Category</span>
            <select
              value={form.categoryId}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  categoryId: Number(event.target.value),
                }))
              }
              className="w-full rounded-2xl border border-stone-300 px-4 py-3"
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>
          <div className="flex gap-3">
            <button
              type="submit"
              className="rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white"
            >
              {editingId ? "Update" : "Create"}
            </button>
            {editingId ? (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm({
                    ...emptyForm,
                    categoryId: categories[0]?.id ?? 0,
                  });
                }}
                className="rounded-2xl border border-stone-300 px-4 py-3 text-sm font-semibold text-stone-700"
              >
                Cancel
              </button>
            ) : null}
          </div>
        </div>
      </form>

      <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
        <h3 className="text-xl font-semibold text-slate-900">Current food items</h3>
        <div className="mt-5 space-y-4">
          {foods.map((food) => (
            <div
              key={food.id}
              className="rounded-2xl border border-stone-200 p-4"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h4 className="text-lg font-semibold text-slate-900">
                      {food.name}
                    </h4>
                    <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-stone-700">
                      {food.status}
                    </span>
                    <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">
                      {food.stockQuantity} units
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-stone-600">
                    {food.categoryName}
                  </p>
                  <p className="mt-3 text-sm text-stone-600">
                    {food.description}
                  </p>
                </div>
                <div className="min-w-36 text-right">
                  <div className="text-lg font-semibold text-slate-900">
                    Rs. {food.price.toFixed(2)}
                  </div>
                  <div className="mt-4 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => beginEdit(food)}
                      className="rounded-2xl border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(food.id)}
                      className="rounded-2xl border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
